import React from 'react';
import { SerializedStyles } from '@emotion/react';
import { markdownInlineToHTML, markdownToHTML } from '$/shared/frontend/utils/MarkdownConverter';
import { css } from '@emotion/react';
import tw from 'twin.macro';
import { propertyFilledText } from '$/components/Chat/utils/injectAttendeeFieldsInMessage';
import { WebinarPlayerUIState, withWebinarPlayerContext } from '$/components/WebinarPlayer/context';
import { useAttendeePropertiesContext } from '$/components/Property/PropertyProvider';
import type { ActionPropertiesHash } from '$/shared/typesProxy';

const DangerouslySetInnerHTML: React.FC<{
  markdownString?: string;
  rawHTML?: string;
  className?: string;
  styles?: SerializedStyles;
  children?: React.ReactNode;
}> = React.memo(({ markdownString, rawHTML, children, className, styles }) => {
  const html = markdownString ? markdownToHTML(markdownString) : rawHTML;
  return (
    <div
      className={`markdown-content ${className}`}
      css={[
        css`
          ${tw`break-words`}
          word-break: break-word;
          p {
            margin: 0;
            padding: 0;
          }
          ul,
          ol {
            li {
              p {
                display: inline;
              }
            }
          }
        `,
        styles,
      ]}
      dangerouslySetInnerHTML={html ? { __html: html } : undefined}
    >
      {children}
    </div>
  );
});

export const FilledMarkdown: React.FC<{
  className?: string;
  styles?: SerializedStyles;
  md: string;
}> = withWebinarPlayerContext(
  ['ewebinar', 'locale'],
  'FilledMarkdown'
)(({ className, styles, md, context }) => {
  const { mentionField, attendee } = useAttendeePropertiesContext();
  const { ewebinar, locale } = context;
  const filledMarkdown = propertyFilledText(
    md,
    locale,
    ewebinar,
    mentionField?.propertiesHash,
    attendee
  );

  return (
    <DangerouslySetInnerHTML
      className={className}
      styles={styles}
      markdownString={filledMarkdown}
    />
  );
});

type PropertyFilledDataContextValue = Pick<
  WebinarPlayerUIState,
  'ewebinar' | 'attendee' | 'locale'
> & {
  propertiesHash: ActionPropertiesHash;
};

export const PropertyFilledDataContext = React.createContext({} as PropertyFilledDataContextValue);
export const PropertyFilledDataContextProvider = withWebinarPlayerContext([
  'ewebinar',
  'attendee',
  'locale',
])(({ context, children }) => {
  const { mentionField } = useAttendeePropertiesContext();
  const value = React.useMemo(
    () => ({
      ...context,
      propertiesHash: mentionField.propertiesHash,
    }),
    [mentionField.propertiesHash, context]
  );
  return (
    <PropertyFilledDataContext.Provider value={value}>
      {children}
    </PropertyFilledDataContext.Provider>
  );
});

export const usePropertyFilledLink = (url: string | null | undefined): string => {
  const { ewebinar, attendee, locale, propertiesHash } =
    React.useContext(PropertyFilledDataContext);

  if (!url) return '';

  if (url.startsWith('data:')) {
    return url;
  }

  if (url.includes('{')) {
    url = propertyFilledText(url, locale, ewebinar, propertiesHash, attendee);
  }

  return markdownInlineToHTML(url);
};

export default DangerouslySetInnerHTML;
